/**
 * useCamera — core camera hook
 *
 * Reusable React hook for browser-based camera capture using getUserMedia.
 * Features: auto-focus (tap-to-focus), zoom (pinch + slider), torch/flash,
 * front/back switching, and a landscape-aware CameraModal component.
 *
 * Usage:
 * ```tsx
 * const camera = useCamera({
 *   onCapture: (base64) => setPhoto(base64),
 *   initialFacingMode: 'environment'
 * });
 *
 * return <CameraModal camera={camera} />;
 * ```
 *
 * @author TotalAccess Development Team
 * @date February 2026
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { hasNativeCameraDirectionIssue } from '@/lib/platformUtils';
import { haptics } from '@/lib/haptics';
import {
  type CameraFacingMode,
  type CameraFacingBucket,
  type CameraFillLightMode,
  type CameraFocusMode,
  type CropRect,
  type CameraDeviceOption,
  type CameraPointOfInterest,
  type ExtendedMediaTrackCapabilities,
  type ExtendedMediaTrackConstraintSet,
  type ExtendedMediaTrackConstraints,
  type ExtendedMediaTrackSettings,
  type ImageCaptureLike,
  type ImageCaptureConstructor,
  type UseCameraConfig,
  type UseCameraReturn,
} from './types';
import { getImageCaptureConstructor, getErrorName, getErrorMessage } from './utils';
import { TORCH_PREF_KEY } from './constants';

export function useCamera(config: UseCameraConfig = {}): UseCameraReturn {
  const {
    onCapture,
    onClose,
    reviewCapture: reviewCaptureEnabled = false,
    initialFacingMode = 'environment',
    quality = 0.85,
    preferredWidth = 1920,
    preferredHeight = 1080,
    videoWidth = 1280,
    videoHeight = 720,
    enableTorchByDefault = false,
    compressOutput,
  } = config;

  // Stable ref so capture() can read the latest config without re-creating
  const compressOutputRef = useRef(compressOutput);
  compressOutputRef.current = compressOutput;

  // Ref so capture() can check reviewCapture without changing its dependency array
  const reviewCaptureRef = useRef(reviewCaptureEnabled);
  reviewCaptureRef.current = reviewCaptureEnabled;

  /**
   * Post-capture compression: load the data URL into an Image, scale to maxDim,
   * re-encode as JPEG. Falls back to the original on any error.
   */
  const compressBase64Image = useCallback(async (
    dataUrl: string,
    maxDim: number,
    compressQuality: number,
  ): Promise<string> => {
    return new Promise((resolve) => {
      const img = document.createElement('img');
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(dataUrl); return; }
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', compressQuality));
        } catch {
          resolve(dataUrl);
        }
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  }, []);

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<CameraFacingMode>(initialFacingMode);
  const [isLoading, setIsLoading] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [isTorchChecking, setIsTorchChecking] = useState(false);

  // ---- Brightness sampling state (for low-light detection) ----
  const [ambientBrightness, setAmbientBrightness] = useState<number | null>(null);
  const autoTorchCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const autoTorchRafRef = useRef<number | null>(null);
  const autoTorchLastSampleRef = useRef<number>(0);

  // ---- Persistence: torch setting saved to localStorage ----
  // Saved on user action (toggleTorch), not on state changes,
  // so the close() reset doesn't overwrite the saved preference.
  const readTorchPref = useCallback((): boolean | null => {
    try {
      const stored = localStorage.getItem(TORCH_PREF_KEY);
      return stored === null ? null : stored === 'true';
    } catch { return null; }
  }, []);

  const saveTorchPref = useCallback((enabled: boolean) => {
    try { localStorage.setItem(TORCH_PREF_KEY, String(enabled)); } catch { /* ignore */ }
  }, []);

  // Zoom state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomMin, setZoomMin] = useState(1);
  const [zoomMax, setZoomMax] = useState(1);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [isDigitalZoom, setIsDigitalZoom] = useState(false);

  // Refs for reading latest zoom state in capture() without re-creating the callback
  const zoomLevelRef = useRef(zoomLevel);
  zoomLevelRef.current = zoomLevel;
  const isDigitalZoomRef = useRef(isDigitalZoom);
  isDigitalZoomRef.current = isDigitalZoom;

  // Focus state
  const [autofocusActive, setAutofocusActive] = useState(true);
  const [availableCameraCount, setAvailableCameraCount] = useState(0);
  const [canSwitchCamera, setCanSwitchCamera] = useState(true);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fillLightModeRef = useRef<CameraFillLightMode>('off');
  const currentDeviceIdRef = useRef<string | null>(null);
  const knownDevicesRef = useRef<CameraDeviceOption[]>([]);
  const preTorchZoomRef = useRef<number>(1);

  const attachVideoElement = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;

    if (node && streamRef.current && node.srcObject !== streamRef.current) {
      node.srcObject = streamRef.current;
      void node.play().catch((playError) => {
        console.warn('[useCamera] Failed to play attached video element:', playError);
      });
    }
  }, []);

  const inferCameraFacingFromLabel = useCallback((label?: string | null): CameraFacingBucket => {
    const normalized = String(label || '').toLowerCase();
    if (!normalized) {
      return 'unknown';
    }

    if (
      normalized.includes('front')
      || normalized.includes('user')
      || normalized.includes('selfie')
      || normalized.includes('facetime')
    ) {
      return 'user';
    }

    if (
      normalized.includes('back')
      || normalized.includes('rear')
      || normalized.includes('environment')
      || normalized.includes('world')
      || normalized.includes('outward')
      || normalized.includes('wide')
      || normalized.includes('ultra')
    ) {
      return 'environment';
    }

    return 'unknown';
  }, []);

  const scoreCameraDeviceForFacing = useCallback((device: CameraDeviceOption, facing: CameraFacingMode): number => {
    const normalizedLabel = device.label.toLowerCase();
    let score = 0;

    if (device.facing === facing) {
      score += 200;
    } else if (device.facing === 'unknown') {
      score += 25;
    } else {
      score -= 200;
    }

    if (facing === 'environment') {
      if (normalizedLabel.includes('wide') || normalizedLabel.includes('ultra')) {
        score += 20;
      }
      if (normalizedLabel.includes('depth') || normalizedLabel.includes('macro')) {
        score -= 20;
      }
    }

    return score;
  }, []);

  const listVideoInputDevices = useCallback(async (): Promise<CameraDeviceOption[]> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      setAvailableCameraCount(0);
      setCanSwitchCamera(false);
      knownDevicesRef.current = [];
      return [];
    }

    const devices = (await navigator.mediaDevices.enumerateDevices())
      .filter((device) => device.kind === 'videoinput' && device.deviceId)
      .map((device) => ({
        deviceId: device.deviceId,
        label: device.label || '',
        facing: inferCameraFacingFromLabel(device.label),
      }));

    knownDevicesRef.current = devices;
    setAvailableCameraCount(devices.length);
    setCanSwitchCamera(devices.length > 1);
    return devices;
  }, [inferCameraFacingFromLabel]);

  const refreshVideoInputDevices = useCallback(async (track?: MediaStreamTrack | null): Promise<CameraDeviceOption[]> => {
    const devices = await listVideoInputDevices();
    const trackDeviceId = track?.getSettings?.().deviceId;
    if (trackDeviceId) {
      currentDeviceIdRef.current = trackDeviceId;
    }
    return devices;
  }, [listVideoInputDevices]);

  const inferTrackFacingMode = useCallback((
    track: MediaStreamTrack,
    devices: CameraDeviceOption[],
    requestedFacing: CameraFacingMode,
  ): CameraFacingBucket => {
    const settingsFacingMode = track.getSettings?.().facingMode;
    if (settingsFacingMode === 'user' || settingsFacingMode === 'environment') {
      return settingsFacingMode;
    }

    const trackLabelFacing = inferCameraFacingFromLabel(track.label);
    if (trackLabelFacing !== 'unknown') {
      return trackLabelFacing;
    }

    const trackDeviceId = track.getSettings?.().deviceId;
    const matchedDevice = trackDeviceId
      ? devices.find((device) => device.deviceId === trackDeviceId)
      : null;

    if (matchedDevice?.facing && matchedDevice.facing !== 'unknown') {
      return matchedDevice.facing;
    }

    return requestedFacing;
  }, [inferCameraFacingFromLabel]);

  const getPreferredDeviceForFacing = useCallback((
    facing: CameraFacingMode,
    devices: CameraDeviceOption[] = knownDevicesRef.current,
    options?: { excludeDeviceId?: string | null },
  ): CameraDeviceOption | null => {
    const candidates = devices.filter((device) => device.deviceId !== options?.excludeDeviceId);
    if (candidates.length === 0) {
      return null;
    }

    return candidates
      .slice()
      .sort((left, right) => scoreCameraDeviceForFacing(right, facing) - scoreCameraDeviceForFacing(left, facing))[0] || null;
  }, [scoreCameraDeviceForFacing]);

  // Helper: get the video track capabilities (safely)
  const getTrackCapabilities = useCallback((): ExtendedMediaTrackCapabilities | null => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return null;
    try {
      return (track.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined) || null;
    } catch {
      return null;
    }
  }, []);

  // Helper: detect zoom range from track capabilities.
  // When `resetLevel` is false, the current zoomLevel state is preserved
  // (used after torch toggles where we want to restore the pre-torch zoom
  // rather than resetting to the new lens minimum).
  const detectZoomCapabilities = useCallback((resetLevel = true) => {
    const caps = getTrackCapabilities();
    if (caps?.zoom && (caps.zoom.max ?? 1) > 1) {
      setZoomMin(caps.zoom.min ?? 1);
      setZoomMax(caps.zoom.max ?? 1);
      setZoomSupported(true);
      setIsDigitalZoom(false);
      if (resetLevel) {
        setZoomLevel(caps.zoom.min ?? 1);
      }
    } else {
      // No native zoom — enable digital zoom fallback via CSS transform
      setZoomMin(1);
      setZoomMax(4);
      setZoomSupported(true);
      setIsDigitalZoom(true);
      if (resetLevel) {
        setZoomLevel(1);
      }
    }
  }, [getTrackCapabilities]);

  const getImageCaptureFillLightModes = useCallback(async (track?: MediaStreamTrack | null): Promise<string[]> => {
    const ImageCaptureCtor = getImageCaptureConstructor();
    if (!track || !ImageCaptureCtor) {
      return [];
    }

    try {
      const imageCapture = new ImageCaptureCtor(track);
      const photoCapabilities = await imageCapture.getPhotoCapabilities?.();
      return Array.isArray(photoCapabilities?.fillLightMode) ? photoCapabilities.fillLightMode : [];
    } catch {
      return [];
    }
  }, []);

  const downscalePhotoBlobToBase64 = useCallback(async (blob: Blob, cropRect?: CropRect): Promise<string> => {
    if (typeof window === 'undefined') {
      throw new Error('Window is not available for photo processing');
    }

    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(blob);
      try {
        let srcX = 0, srcY = 0, srcW = bitmap.width, srcH = bitmap.height;
        if (cropRect) {
          srcX = Math.round(cropRect.x * bitmap.width);
          srcY = Math.round(cropRect.y * bitmap.height);
          srcW = Math.round(cropRect.width * bitmap.width);
          srcH = Math.round(cropRect.height * bitmap.height);
        }

        const scale = Math.min(
          1,
          preferredWidth / Math.max(srcW, 1),
          preferredHeight / Math.max(srcH, 1),
        );

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(srcW * scale));
        canvas.height = Math.max(1, Math.round(srcH * scale));

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Failed to get canvas context for photo processing');
        }

        ctx.drawImage(bitmap, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL('image/jpeg', quality);
      } finally {
        bitmap.close();
      }
    }

    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert photo blob to base64'));
        }
      };
      reader.onerror = () => reject(reader.error || new Error('Failed to read photo blob'));
      reader.readAsDataURL(blob);
    });
  }, [preferredWidth, preferredHeight, quality]);

  const detectTorchSupport = useCallback(async (track?: MediaStreamTrack | null): Promise<boolean> => {
    if (!track) return false;

    try {
      const caps = track.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
      if (caps?.torch !== undefined) {
        return true;
      }
    } catch {
      // Ignore and fall back to ImageCapture probing below.
    }

    const fillLightModes = await getImageCaptureFillLightModes(track);
    return fillLightModes.includes('torch') || fillLightModes.includes('flash');
  }, [getImageCaptureFillLightModes]);

  const setTorchState = useCallback(async (
    enabled: boolean,
    trackOverride?: MediaStreamTrack | null,
    zoomToPreserve?: number,
  ): Promise<boolean> => {
    const videoTrack = trackOverride ?? streamRef.current?.getVideoTracks()[0];
    if (!videoTrack) {
      setTorchEnabled(false);
      fillLightModeRef.current = 'off';
      return false;
    }

    try {
      // Apply torch and zoom together in a single applyConstraints call.
      // On multi-lens phones, applying torch alone can cause the browser to
      // switch to a torch-capable lens, which resets zoom to that lens's
      // minimum (often 2× or 3×). By including the current zoom in the same
      // constraint set, we signal to the browser that we want to stay at the
      // current zoom level, preventing the jarring zoom jump.
      // Skip the zoom constraint when using digital zoom (no native zoom support)
      // to avoid OverconstrainedError on devices that don't support zoom constraints.
      const advanced: ExtendedMediaTrackConstraintSet[] = [{ torch: enabled }];
      if (zoomToPreserve !== undefined && zoomToPreserve > 0 && !isDigitalZoomRef.current) {
        advanced.push({ zoom: zoomToPreserve });
      }
      await videoTrack.applyConstraints({ advanced } as ExtendedMediaTrackConstraints);

      // Verify torch actually activated (some devices report success but don't turn on)
      if (enabled) {
        const settings = videoTrack.getSettings() as ExtendedMediaTrackSettings;
        if (settings.torch !== true) {
          // Retry once after 300ms
          await new Promise(r => setTimeout(r, 300));
          await videoTrack.applyConstraints({ advanced } as ExtendedMediaTrackConstraints);
          const retrySettings = videoTrack.getSettings() as ExtendedMediaTrackSettings;
          if (retrySettings.torch !== true) {
            console.warn('[useCamera] Torch applyConstraints succeeded but torch not active in settings');
            setTorchEnabled(false);
            fillLightModeRef.current = 'off';
            return false;
          }
        }
      }

      setTorchSupported(true);
      setTorchEnabled(enabled);
      fillLightModeRef.current = enabled ? 'torch' : 'off';
      console.log('[useCamera] Torch', enabled ? 'ON' : 'OFF', '(constraints)', zoomToPreserve !== undefined ? `zoom=${zoomToPreserve}` : '');
      return true;
    } catch (constraintError) {
      console.warn('[useCamera] Track torch constraint failed, trying ImageCapture fallback:', constraintError);
    }

    const fillLightModes = await getImageCaptureFillLightModes(videoTrack);
    const ImageCaptureCtor = getImageCaptureConstructor();
    if (fillLightModes.length > 0 && ImageCaptureCtor) {
      try {
        const preferredMode = enabled
          ? (fillLightModes.includes('torch') ? 'torch' : fillLightModes.includes('flash') ? 'flash' : null)
          : 'off';

        if (preferredMode) {
          const imageCapture = new ImageCaptureCtor(videoTrack);
          await imageCapture.setOptions({ fillLightMode: preferredMode });
          setTorchSupported(true);
          setTorchEnabled(enabled);
          fillLightModeRef.current = enabled ? preferredMode : 'off';
          console.log('[useCamera] Torch', enabled ? 'ON' : 'OFF', `(ImageCapture:${preferredMode})`);
          return true;
        }
      } catch (icError) {
        console.warn('[useCamera] ImageCapture torch fallback failed:', icError);
      }
    }

    setTorchSupported(false);
    setTorchEnabled(false);
    fillLightModeRef.current = 'off';
    return false;
  }, [getImageCaptureFillLightModes]);

  // Directly set the torch hardware state without persisting the user
  // preference. Used by CameraModal to turn the torch off while the user is
  // reviewing a captured photo, and to restore it when they return to the
  // live camera.
  const setTorch = useCallback(async (enabled: boolean): Promise<boolean> => {
    const zoomToPreserve = isDigitalZoomRef.current ? undefined : zoomLevelRef.current;
    return setTorchState(enabled, null, zoomToPreserve);
  }, [setTorchState]);

  const getTorchCandidateDevices = useCallback(async (): Promise<MediaDeviceInfo[]> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      return [];
    }

    const currentDeviceId = streamRef.current?.getVideoTracks?.()[0]?.getSettings?.().deviceId;
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter((device) => device.kind === 'videoinput' && device.deviceId);

    const scoreDevice = (device: MediaDeviceInfo, index: number): number => {
      const label = device.label.toLowerCase();
      const inferredFacing = inferCameraFacingFromLabel(device.label);
      let score = 0;

      if (inferredFacing === 'environment' || label.includes('back') || label.includes('rear') || label.includes('environment') || label.includes('world')) {
        score += 100;
      }
      if (inferredFacing === 'user' || label.includes('front') || label.includes('user') || label.includes('selfie')) {
        score -= 100;
      }
      if (label.includes('wide') || label.includes('ultra')) {
        score += 10;
      }

      return score - index;
    };

    return videoInputs
      .filter((device) => device.deviceId !== currentDeviceId)
      .sort((left, right) => scoreDevice(right, 0) - scoreDevice(left, 0));
  }, [inferCameraFacingFromLabel]);

  const scheduleTrackSetup = useCallback((facing: CameraFacingMode) => {
    setTimeout(() => {
      detectZoomCapabilities();

      void (async () => {
        const settledTrack = streamRef.current?.getVideoTracks()[0];
        if (!settledTrack) return;

        // Explicitly set zoom to minimum (widest FOV) after camera settles.
        // Some phones default to a zoomed-in lens; this ensures we start at 1x.
        // IMPORTANT: Include the current torch state in the same applyConstraints
        // call. Applying zoom alone can reset the torch constraint on some
        // devices (e.g. Samsung S24 WebView), turning the physical torch off
        // while torchEnabled state remains true.
        // Use fillLightModeRef (not torchEnabledRef) because fillLightModeRef
        // is declared before scheduleTrackSetup in the hook scope.
        try {
          const caps = settledTrack.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
          if (caps?.zoom && (caps.zoom.max ?? 1) > 1) {
            const minZoom = caps.zoom.min ?? 1;
            const torchWasOn = fillLightModeRef.current !== 'off';
            const advanced: ExtendedMediaTrackConstraintSet[] = [{ zoom: minZoom }];
            if (torchWasOn) {
              advanced.push({ torch: true });
            }
            try {
              await settledTrack.applyConstraints({
                advanced,
              } as ExtendedMediaTrackConstraints);
            } catch (combinedErr) {
              // Combined zoom+torch failed — try zoom only (some devices
              // don't report torch in capabilities but still accept it
              // in applyConstraints, while others reject it entirely).
              if (torchWasOn) {
                try {
                  await settledTrack.applyConstraints({
                    advanced: [{ zoom: minZoom }] as ExtendedMediaTrackConstraintSet[],
                  } as ExtendedMediaTrackConstraints);
                } catch (zoomErr) {
                  // Zoom constraint also failed — device reports zoom in
                  // capabilities but rejects applyConstraints. Fall back to digital.
                  console.warn('[useCamera] Native zoom applyConstraints failed, using digital zoom:', zoomErr instanceof Error ? zoomErr.message : String(zoomErr));
                  setIsDigitalZoom(true);
                  setZoomMin(1);
                  setZoomMax(4);
                }
                // Re-apply torch separately after zoom
                try {
                  await settledTrack.applyConstraints({
                    advanced: [{ torch: true }] as ExtendedMediaTrackConstraintSet[],
                  } as ExtendedMediaTrackConstraints);
                } catch {
                  // Torch re-apply failed — torch may have been reset
                  console.warn('[useCamera] Torch re-apply after zoom failed');
                }
              } else {
                // No torch to preserve — zoom failed on its own. Fall back to digital.
                console.warn('[useCamera] Native zoom applyConstraints failed, using digital zoom:', combinedErr instanceof Error ? combinedErr.message : String(combinedErr));
                setIsDigitalZoom(true);
                setZoomMin(1);
                setZoomMax(4);
              }
            }
            setZoomLevel(minZoom);
            console.log('[useCamera] Zoom set to minimum (widest FOV):', minZoom);
          }
        } catch (zoomErr) {
          console.warn('[useCamera] Failed to set minimum zoom:', zoomErr);
        }

        const supported = await detectTorchSupport(settledTrack);
        setTorchSupported(supported);

        if (supported) {
          console.log('[useCamera] Torch confirmed supported after settle');
        }

        // Torch enable/disable is now handled by startCameraInternal (within
        // the user's transient activation window) and by CameraModal's torch
        // mode effects. We no longer try to restore torch here — the 500ms
        // setTimeout breaks the user gesture chain in WebViews, causing
        // applyConstraints({ torch: true }) to fail silently.
      })();
    }, 500);
  }, [detectZoomCapabilities, detectTorchSupport]);

  const startCameraInternal = useCallback(async (
    facing: CameraFacingMode,
    options?: { preferredDeviceId?: string; allowDeviceRecovery?: boolean },
  ) => {
    console.log('[useCamera] Starting camera with facingMode:', facing);
    setError(null);
    setIsLoading(true);

    try {
      // Stop any existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        setStream(null);
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this device');
      }

      const constraints: MediaStreamConstraints = {
        video: options?.preferredDeviceId
          ? {
            deviceId: { exact: options.preferredDeviceId },
            // Use moderate resolution for the live stream to get the widest
            // field of view. High-res requests (e.g. 1440x1920) cause many
            // phones to select a narrower-FOV lens or apply digital zoom.
            // High resolution is only needed for capture, not the live preview.
            width: { ideal: videoWidth },
            height: { ideal: videoHeight },
          }
          : {
            facingMode: { ideal: facing },
            width: { ideal: videoWidth },
            height: { ideal: videoHeight },
          },
        audio: false,
      };

      console.log('[useCamera] Requesting camera with constraints:', constraints);
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      const track = mediaStream.getVideoTracks()[0];
      currentDeviceIdRef.current = track?.getSettings?.().deviceId || options?.preferredDeviceId || null;

      const devices = await refreshVideoInputDevices(track);
      const actualFacing = inferTrackFacingMode(track, devices, facing);
      const preferredFacingDevice = getPreferredDeviceForFacing(facing, devices, {
        excludeDeviceId: currentDeviceIdRef.current,
      });

      const shouldRetryWithExplicitDevice = Boolean(
        options?.allowDeviceRecovery !== false
        && preferredFacingDevice
        && preferredFacingDevice.deviceId !== currentDeviceIdRef.current
        && (
          actualFacing !== facing
          || (hasNativeCameraDirectionIssue() && preferredFacingDevice.facing === facing)
        ),
      );

      if (shouldRetryWithExplicitDevice && preferredFacingDevice) {
        console.warn('[useCamera] Facing mode selected the wrong camera; retrying with explicit deviceId', {
          requestedFacing: facing,
          actualFacing,
          currentDeviceId: currentDeviceIdRef.current,
          preferredDeviceId: preferredFacingDevice.deviceId,
          preferredDeviceLabel: preferredFacingDevice.label,
        });
        mediaStream.getTracks().forEach((streamTrack) => streamTrack.stop());
        return await startCameraInternal(facing, {
          preferredDeviceId: preferredFacingDevice.deviceId,
          allowDeviceRecovery: false,
        });
      }

      streamRef.current = mediaStream;
      setFacingMode(facing);
      setTorchEnabled(false);
      fillLightModeRef.current = 'off';
      // Don't assume torch support — we'll check capabilities after track settles
      setTorchSupported(false);

      // Enable continuous autofocus if supported
      try {
        const caps = track.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
        if (caps?.focusMode?.includes('continuous')) {
          await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] as ExtendedMediaTrackConstraintSet[] } as ExtendedMediaTrackConstraints);
          setAutofocusActive(true);
          console.log('[useCamera] Continuous autofocus enabled');
        } else {
          setAutofocusActive(false);
        }
        if (caps?.torch !== undefined) {
          setTorchSupported(true);
          console.log('[useCamera] Torch supported (from capabilities)');
        }
      } catch {
        setAutofocusActive(false);
      }

      // Attach to video element BEFORE calling setStream().
      // usePdf417Detection's effect runs when `stream` changes, and it
      // needs srcObject to already be set on the video element.
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
        console.log('[useCamera] Camera started successfully');
      }

      // NOW trigger re-render — usePdf417Detection effect will see
      // srcObject already set on the video element.
      setStream(mediaStream);

      scheduleTrackSetup(facing);
    } catch (err: unknown) {
      console.error('[useCamera] Camera error:', err);
      const errorName = getErrorName(err);
      const errorMessage = getErrorMessage(err, 'Failed to access camera');

      if (options?.preferredDeviceId) {
        console.warn('[useCamera] Explicit device open failed, falling back to facingMode selection:', options.preferredDeviceId, err);
        return await startCameraInternal(facing, { allowDeviceRecovery: false });
      }

      // Try fallback without facingMode constraint
      if (errorName === 'OverconstrainedError' || errorName === 'ConstraintNotSatisfiedError') {
        console.log('[useCamera] Trying fallback without facingMode...');
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: videoWidth }, height: { ideal: videoHeight } },
            audio: false,
          });
          const fallbackTrack = fallbackStream.getVideoTracks()[0];
          currentDeviceIdRef.current = fallbackTrack?.getSettings?.().deviceId || null;
          await refreshVideoInputDevices(fallbackTrack);
          streamRef.current = fallbackStream;
          setFacingMode(facing);
          setTorchEnabled(false);
          fillLightModeRef.current = 'off';
          setTorchSupported(false);
          if (videoRef.current) {
            videoRef.current.srcObject = fallbackStream;
            await videoRef.current.play();
            console.log('[useCamera] Camera started with fallback');
          }
          // Set stream state AFTER srcObject is attached (same fix as primary path)
          setStream(fallbackStream);
          scheduleTrackSetup(facing);
          setIsLoading(false);
          return;
        } catch (fallbackError) {
          console.error('[useCamera] Fallback also failed:', fallbackError);
        }
      }

      if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access in your browser/app settings.');
      } else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    getPreferredDeviceForFacing,
    inferTrackFacingMode,
    refreshVideoInputDevices,
    scheduleTrackSetup,
    videoWidth,
    videoHeight,
  ]);

  // Start camera stream
  const startCamera = useCallback(async (facing: CameraFacingMode = facingMode) => {
    await startCameraInternal(facing, { allowDeviceRecovery: true });
  }, [facingMode, startCameraInternal]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    console.log('[useCamera] Stopping camera');
    if (streamRef.current) {
      // Explicitly turn off the torch before stopping the track.
      // On some devices (especially Capacitor WebView), track.stop() does
      // not reliably turn off the physical torch. We fire-and-forget the
      // applyConstraints call — it will either complete before the track
      // is stopped, or the track will be stopped and the call will no-op.
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack && videoTrack.readyState === 'live') {
        try {
          void videoTrack.applyConstraints({
            advanced: [{ torch: false }] as ExtendedMediaTrackConstraintSet[],
          } as ExtendedMediaTrackConstraints).catch(() => {
            // Track may be stopped by the time this resolves — ignore
          });
        } catch {
          // applyConstraints can throw synchronously if track is ending — ignore
        }
      }
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setStream(null);
    }
    fillLightModeRef.current = 'off';
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setZoomLevel(1);
    setIsDigitalZoom(false);
  }, []);

  const recoverTorchCapableCamera = useCallback(async (requiredZoom?: number): Promise<boolean> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      return false;
    }

    const candidates = await getTorchCandidateDevices();
    if (candidates.length === 0) {
      console.warn('[useCamera] No alternate video input devices available for torch recovery');
      return false;
    }

    const previousStream = streamRef.current;
    const previousFacingMode = facingMode;

    if (previousStream) {
      previousStream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setStream(null);
    }

    for (const candidate of candidates) {
      try {
        console.log('[useCamera] Probing alternate camera for torch support:', candidate.label || candidate.deviceId);
        const candidateStream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: candidate.deviceId },
            width: { ideal: videoWidth },
            height: { ideal: videoHeight },
          },
          audio: false,
        });

        const candidateTrack = candidateStream.getVideoTracks()[0];
        const torchAvailable = await detectTorchSupport(candidateTrack);

        console.log('[useCamera] Alternate camera torch probe result:', {
          label: candidate.label || candidate.deviceId,
          deviceId: candidate.deviceId,
          torchAvailable,
          settings: candidateTrack.getSettings?.(),
        });

        if (torchAvailable) {
          // If a zoom level must be preserved (e.g. torch-on switched to a
          // narrower lens), skip candidates whose minimum zoom exceeds it —
          // switching to another narrow lens would not restore the framing.
          const probeCaps = candidateTrack.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
          const probeMinZoom = probeCaps?.zoom?.min ?? 1;
          if (requiredZoom !== undefined && probeMinZoom > requiredZoom + 0.01) {
            console.log('[useCamera] Skipping alternate camera — min zoom exceeds required zoom:', {
              label: candidate.label || candidate.deviceId,
              probeMinZoom,
              requiredZoom,
            });
            candidateStream.getTracks().forEach((track) => track.stop());
            continue;
          }

          streamRef.current = candidateStream;
          setFacingMode('environment');
          setTorchEnabled(false);
          fillLightModeRef.current = 'off';
          setTorchSupported(true);

          if (videoRef.current) {
            videoRef.current.srcObject = candidateStream;
            await videoRef.current.play();
          }

          // Set stream state AFTER srcObject is attached (same fix as primary path)
          setStream(candidateStream);

          // Restore zoom after switching to torch-capable camera.
          // Use resetLevel=false so detectZoomCapabilities doesn't clobber
          // our restored zoom with the new lens minimum.
          detectZoomCapabilities(false);
          const savedZoom = preTorchZoomRef.current;
          const newTrack = candidateStream.getVideoTracks()[0];
          const caps = newTrack.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
          const min = caps?.zoom?.min ?? 1;
          const max = caps?.zoom?.max ?? 1;
          const clampedZoom = Math.min(Math.max(savedZoom, min), max);
          try {
            await newTrack.applyConstraints({ advanced: [{ zoom: clampedZoom }] as ExtendedMediaTrackConstraintSet[] } as ExtendedMediaTrackConstraints);
            setZoomLevel(clampedZoom);
          } catch {
            setZoomLevel(min);
          }

          scheduleTrackSetup('environment');
          return true;
        }

        candidateStream.getTracks().forEach((track) => track.stop());
      } catch (recoveryError) {
        console.warn('[useCamera] Failed probing alternate camera for torch support:', recoveryError);
      }
    }

    console.warn('[useCamera] No torch-capable alternate camera found; restoring previous facing mode');
    await startCamera(previousFacingMode);
    return false;
  }, [detectTorchSupport, facingMode, getTorchCandidateDevices, scheduleTrackSetup, startCamera, detectZoomCapabilities, videoWidth, videoHeight]);

  // Open camera
  const open = useCallback(() => {
    console.log('[useCamera] Opening camera');
    setIsOpen(true);

    if (!streamRef.current && !isLoading) {
      void startCamera(facingMode);
    }
  }, [facingMode, isLoading, startCamera]);

  // Close camera
  const close = useCallback(() => {
    console.log('[useCamera] Closing camera');
    stopCamera();
    setIsOpen(false);
    setError(null);
    // Reset torch state — the physical torch is off because stopCamera() stops the track.
    // The user's torch preference is persisted in localStorage via saveTorchPref,
    // and restored on next startCamera.
    setTorchEnabled(false);
    setTorchSupported(false);
    fillLightModeRef.current = 'off';
    onClose?.();
  }, [stopCamera, onClose]);

  // Toggle torch / flash
  const toggleTorch = useCallback(async () => {
    if (!streamRef.current?.getVideoTracks()[0]) {
      console.warn('[useCamera] No video track for torch toggle');
      return;
    }

    setIsTorchChecking(true);
    // Save current zoom so we can restore it after torch toggle
    // (some browsers reset zoom or switch lenses when torch is applied)
    preTorchZoomRef.current = zoomLevel;
    const savedZoom = zoomLevel;
    const newVal = !torchEnabled;
    // Pass the current zoom to setTorchState so it is applied in the same
    // applyConstraints call as torch — this prevents the browser from
    // switching lenses and resetting zoom to 2×/3×.
    // Skip zoom preservation on devices using digital zoom (no native zoom
    // support) — including zoom in applyConstraints throws on those devices.
    const zoomToPreserve = isDigitalZoomRef.current ? undefined : savedZoom;
    let success = await setTorchState(newVal, null, zoomToPreserve);

    if (!success && newVal && facingMode === 'environment') {
      console.warn('[useCamera] Torch enable failed on current track; attempting alternate back-camera recovery');
      const recovered = await recoverTorchCapableCamera(savedZoom);
      if (recovered) {
        success = await setTorchState(true, null, zoomToPreserve);
      }
    }

    // Re-detect zoom capabilities WITHOUT resetting the zoom level, then
    // restore the pre-torch zoom. Applying torch can make the UA switch to a
    // narrower torch-capable lens whose zoom.min exceeds the saved zoom —
    // so apply the saved zoom VERBATIM first rather than clamping up to the
    // reported minimum (which would lock in the zoom jump). Many UAs report
    // the switched lens's range but still honour the lower zoom.
    // Skip this for digital zoom — CSS transform handles the visual zoom
    // and applyConstraints with zoom would throw on unsupported devices.
    if (success && !isDigitalZoomRef.current) {
      detectZoomCapabilities(false); // false = don't reset zoomLevel to min
      const track = streamRef.current?.getVideoTracks()[0];
      if (track) {
        const caps = track.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
        const min = caps?.zoom?.min ?? 1;
        const max = caps?.zoom?.max ?? 1;
        try {
          await track.applyConstraints({ advanced: [{ zoom: savedZoom }] as ExtendedMediaTrackConstraintSet[] } as ExtendedMediaTrackConstraints);
        } catch {
          // Constraint rejected — verify the effective zoom below.
        }
        const appliedZoom = (track.getSettings?.() as ExtendedMediaTrackSettings | undefined)?.zoom;
        // Treat the zoom as restored when settings confirm it, or when the
        // track can't report zoom but its minimum still allows savedZoom.
        const zoomRestored = appliedZoom === undefined
          ? min <= savedZoom + 0.01
          : appliedZoom <= savedZoom + 0.05;

        if (zoomRestored) {
          setZoomLevel(savedZoom);
        } else if (facingMode === 'environment') {
          // The lens genuinely cannot reach the pre-torch zoom.
          if (newVal) {
            // Torch-on switched to a narrower torch-capable lens — probe
            // alternates that support torch AND can still reach savedZoom.
            const recovered = await recoverTorchCapableCamera(savedZoom);
            if (recovered) {
              success = await setTorchState(newVal, null, savedZoom);
              if (success) setZoomLevel(savedZoom);
            } else {
              // Recovery failed and restarted the original camera — the
              // physical torch is off even though the user asked for it.
              setTorchEnabled(false);
              fillLightModeRef.current = 'off';
              success = false;
            }
          } else {
            // Torch-off left us on the narrower torch lens — restart the
            // camera so the UA re-selects the default (wide) lens.
            // scheduleTrackSetup settles the new track at its own minimum.
            await startCamera(facingMode);
          }
          if (newVal && !success) {
            // Fall back to the closest available zoom on the current track.
            const fallbackTrack = streamRef.current?.getVideoTracks()[0];
            const fallbackCaps = fallbackTrack?.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
            const fMin = fallbackCaps?.zoom?.min ?? min;
            const fMax = fallbackCaps?.zoom?.max ?? max;
            const clampedZoom = Math.min(Math.max(savedZoom, fMin), fMax);
            try {
              await fallbackTrack?.applyConstraints({ advanced: [{ zoom: clampedZoom }] as ExtendedMediaTrackConstraintSet[] } as ExtendedMediaTrackConstraints);
              setZoomLevel(clampedZoom);
            } catch {
              setZoomLevel(fMin);
            }
          }
        } else {
          // Front camera — no alternates to probe; use the closest zoom.
          const clampedZoom = Math.min(Math.max(savedZoom, min), max);
          try {
            await track.applyConstraints({ advanced: [{ zoom: clampedZoom }] as ExtendedMediaTrackConstraintSet[] } as ExtendedMediaTrackConstraints);
            setZoomLevel(clampedZoom);
          } catch {
            setZoomLevel(min);
          }
        }
      }
    }

    if (!success) {
      console.warn('[useCamera] Torch toggle failed');
    }

    // Persist the user's torch preference
    const turningOn = !torchEnabled;
    saveTorchPref(turningOn);

    setIsTorchChecking(false);
  }, [torchEnabled, setTorchState, facingMode, recoverTorchCapableCamera, zoomLevel, detectZoomCapabilities, saveTorchPref, startCamera]);

  // ---- Brightness sampling (for low-light detection) ----
  // Samples the video feed every ~500ms to detect low-light conditions.
  // Used by CameraModal for low-light warnings and post-capture enhancement.
  useEffect(() => {
    if (!isOpen || facingMode !== 'environment') {
      setAmbientBrightness(null);
      if (autoTorchRafRef.current !== null) {
        cancelAnimationFrame(autoTorchRafRef.current);
        autoTorchRafRef.current = null;
      }
      return;
    }

    const sample = () => {
      const video = videoRef.current;
      if (!video || !video.videoWidth || !video.videoHeight) return;

      const sW = 32;
      const sH = Math.round(sW * (video.videoHeight / video.videoWidth));
      if (!autoTorchCanvasRef.current) {
        autoTorchCanvasRef.current = document.createElement('canvas');
      }
      const canvas = autoTorchCanvasRef.current;
      canvas.width = sW;
      canvas.height = sH;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      try {
        ctx.drawImage(video, 0, 0, sW, sH);
        const data = ctx.getImageData(0, 0, sW, sH).data;
        let sum = 0;
        const count = sW * sH;
        for (let i = 0; i < data.length; i += 4) {
          sum += (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
        }
        const lum = sum / count;
        setAmbientBrightness(lum);
      } catch {
        // Video not ready
      }
    };

    const tick = () => {
      autoTorchRafRef.current = requestAnimationFrame(tick);
      const now = performance.now();
      if (now - autoTorchLastSampleRef.current < 500) return;
      autoTorchLastSampleRef.current = now;
      sample();
    };
    autoTorchRafRef.current = requestAnimationFrame(tick);

    return () => {
      if (autoTorchRafRef.current !== null) {
        cancelAnimationFrame(autoTorchRafRef.current);
        autoTorchRafRef.current = null;
      }
    };
  }, [isOpen, facingMode]);

  // Set zoom level
  const setZoom = useCallback(async (level: number) => {
    const clamped = Math.min(Math.max(level, zoomMin), zoomMax);
    if (isDigitalZoom) {
      // Digital zoom: no native API, just update state (CSS transform handles visual)
      setZoomLevel(clamped);
      return;
    }
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ zoom: clamped }] as ExtendedMediaTrackConstraintSet[] } as ExtendedMediaTrackConstraints);
      setZoomLevel(clamped);
    } catch (err) {
      // OverconstrainedError means the track doesn't actually support native
      // zoom constraints (some browsers report zoom in getCapabilities() but
      // reject applyConstraints). Switch to digital zoom mode so we stop
      // spamming applyConstraints on every animation frame.
      if (getErrorName(err) === 'OverconstrainedError') {
        console.warn('[useCamera] Native zoom unsupported, switching to digital zoom');
        setIsDigitalZoom(true);
        setZoomMin(1);
        setZoomMax(4);
      } else {
        console.warn('[useCamera] Zoom failed:', err);
      }
      // Still update the zoom level state so digital zoom CSS transform works
      setZoomLevel(clamped);
    }
  }, [zoomMin, zoomMax, isDigitalZoom]);

  // Tap-to-focus: set focus point and mode to manual/single-shot then back to continuous
  const tapToFocus = useCallback(async (x: number, y: number, lock = false) => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      const caps = track.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
      // Try pointsOfInterest + manual focus first
      if (caps?.focusMode?.includes('manual') || caps?.focusMode?.includes('single-shot')) {
        const mode: CameraFocusMode = caps.focusMode.includes('single-shot') ? 'single-shot' : 'manual';
        const advanced: ExtendedMediaTrackConstraintSet[] = [{ focusMode: mode }];
        // pointsOfInterest is supported on Android Chrome / some browsers
        if (caps?.pointsOfInterest) {
          advanced.push({ pointsOfInterest: [{ x, y }] });
        }
        await track.applyConstraints({ advanced } as ExtendedMediaTrackConstraints);
        setAutofocusActive(false);
        console.log(`[useCamera] Tap-to-focus at (${x.toFixed(2)}, ${y.toFixed(2)}), mode=${mode}${lock ? ' (locked)' : ''}`);

        // Return to continuous autofocus after 3 seconds only if not in manual lock mode
        if (!lock) {
          setTimeout(async () => {
            try {
              if (streamRef.current && caps?.focusMode?.includes('continuous')) {
                const t = streamRef.current.getVideoTracks()[0];
                if (t) {
                  await t.applyConstraints({ advanced: [{ focusMode: 'continuous' }] as ExtendedMediaTrackConstraintSet[] } as ExtendedMediaTrackConstraints);
                  setAutofocusActive(true);
                }
              }
            } catch { /* ignore */ }
          }, 3000);
        }
      }
    } catch (err) {
      console.warn('[useCamera] Tap-to-focus failed:', err);
    }
  }, []);

  // Toggle between continuous autofocus (AF) and manual focus (MF)
  const toggleFocusMode = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      const caps = track.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
      if (!caps?.focusMode) return;
      if (autofocusActive) {
        // Switch to manual focus: use single-shot if available so the lens
        // actually pulls focus at the center point and then holds. If the
        // device only advertises 'manual', we still lock at the current focus.
        const mode: CameraFocusMode = caps.focusMode.includes('single-shot') ? 'single-shot' : 'manual';
        const advanced: ExtendedMediaTrackConstraintSet[] = [{ focusMode: mode }];
        // Pull focus at the center of the frame to give manual mode a concrete
        // focus point instead of leaving it wherever continuous AF happened to be.
        if (caps?.pointsOfInterest) {
          advanced.push({ pointsOfInterest: [{ x: 0.5, y: 0.5 }] });
        }
        await track.applyConstraints({ advanced } as ExtendedMediaTrackConstraints);
        setAutofocusActive(false);
        console.log('[useCamera] Focus mode switched to MF at center');
      } else {
        // Switch to continuous autofocus
        if (caps.focusMode.includes('continuous')) {
          await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] as ExtendedMediaTrackConstraintSet[] } as ExtendedMediaTrackConstraints);
          setAutofocusActive(true);
          console.log('[useCamera] Focus mode switched to AF');
        }
      }
    } catch (err) {
      console.warn('[useCamera] Focus mode toggle failed:', err);
    }
  }, [autofocusActive]);

  // Switch camera facing mode
  const switchCamera = useCallback(async () => {
    const newFacing = facingMode === 'user' ? 'environment' : 'user';
    console.log('[useCamera] Switching camera to:', newFacing);
    void haptics.medium();
    const preferredDevice = getPreferredDeviceForFacing(newFacing, knownDevicesRef.current, {
      excludeDeviceId: currentDeviceIdRef.current,
    });

    if (preferredDevice) {
      await startCameraInternal(newFacing, {
        preferredDeviceId: preferredDevice.deviceId,
        allowDeviceRecovery: false,
      });
      return;
    }

    await startCamera(newFacing);
  }, [facingMode, getPreferredDeviceForFacing, startCamera, startCameraInternal]);

  // Capture photo from video stream
  const capture = useCallback(async (cropRect?: CropRect): Promise<string | null> => {
    console.log('[useCamera] Capturing photo', cropRect ? '(cropped)' : '');
    if (!videoRef.current || !streamRef.current) {
      console.error('[useCamera] Camera not ready for capture');
      return null;
    }

    const activeTrack = streamRef.current.getVideoTracks()[0];
    const activeFillLightMode = fillLightModeRef.current;

    const ImageCaptureCtor = getImageCaptureConstructor();
    if (activeTrack && activeFillLightMode === 'flash' && ImageCaptureCtor) {
      try {
        const imageCapture = new ImageCaptureCtor(activeTrack);
        const blob = await imageCapture.takePhoto({ fillLightMode: 'flash' });
        let base64 = await downscalePhotoBlobToBase64(blob, cropRect);
        const comp = compressOutputRef.current;
        if (comp) {
          base64 = await compressBase64Image(base64, comp.maxDim ?? 1280, comp.quality ?? 0.68);
        }
        console.log('[useCamera] Photo captured via ImageCapture flash, size:', base64.length);
        if (!reviewCaptureRef.current) onCapture?.(base64);
        return base64;
      } catch (err) {
        console.warn('[useCamera] Flash photo capture failed, falling back to video frame:', err);
      }
    }

    const video = videoRef.current;
    const vw = video.videoWidth || 1280;
    const vh = video.videoHeight || 720;

    // Source region — full frame or cropped
    let srcX = 0, srcY = 0, srcW = vw, srcH = vh;
    if (cropRect) {
      srcX = Math.round(cropRect.x * vw);
      srcY = Math.round(cropRect.y * vh);
      srcW = Math.round(cropRect.width * vw);
      srcH = Math.round(cropRect.height * vh);
    }

    // Apply digital zoom center-crop when native zoom is unavailable
    const dz = isDigitalZoomRef.current ? zoomLevelRef.current : 1;
    if (dz > 1) {
      const dzW = vw / dz;
      const dzH = vh / dz;
      const dzX = (vw - dzW) / 2;
      const dzY = (vh - dzH) / 2;
      if (cropRect) {
        // Map cropRect into the zoomed region
        srcX = Math.round(dzX + cropRect.x * dzW);
        srcY = Math.round(dzY + cropRect.y * dzH);
        srcW = Math.round(cropRect.width * dzW);
        srcH = Math.round(cropRect.height * dzH);
      } else {
        srcX = Math.round(dzX);
        srcY = Math.round(dzY);
        srcW = Math.round(dzW);
        srcH = Math.round(dzH);
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, srcW);
    canvas.height = Math.max(1, srcH);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('[useCamera] Failed to get canvas context');
      return null;
    }

    ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
    let base64 = canvas.toDataURL('image/jpeg', quality);

    // Optional post-capture compression
    const comp = compressOutputRef.current;
    if (comp) {
      base64 = await compressBase64Image(base64, comp.maxDim ?? 1280, comp.quality ?? 0.68);
    }

    console.log('[useCamera] Photo captured, size:', base64.length);
    if (!reviewCaptureRef.current) onCapture?.(base64);
    return base64;
  }, [quality, onCapture, downscalePhotoBlobToBase64, compressBase64Image]);

  // Submit a photo to onCapture — used by CameraModal when reviewCapture defers the callback
  const submitPhoto = useCallback((base64: string) => {
    onCapture?.(base64);
  }, [onCapture]);

  // Cleanup camera when closed
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
    }
  }, [isOpen, stopCamera]);

  // Handle hardware back button on Android/Capacitor
  // NOTE: Back button is now handled via ModalStateContext registerBackHandler in CameraModal
  // The direct App.addListener approach caused double-firing with the global handler in 
  // useCapacitorMobile, which would navigate away or show exit confirmation.

  return {
    isOpen,
    open,
    close,
    videoRef,
    attachVideoElement,
    stream,
    error,
    facingMode,
    switchCamera,
    canSwitchCamera,
    availableCameraCount,
    capture,
    isLoading,
    startCamera,
    torchEnabled,
    torchSupported,
    isTorchChecking,
    toggleTorch,
    setTorch,
    ambientBrightness,
    zoomLevel,
    zoomMin,
    zoomMax,
    zoomSupported,
    isDigitalZoom,
    setZoom,
    tapToFocus,
    autofocusActive,
    toggleFocusMode,
    streamRef,
    submitPhoto,
  };
}

export default useCamera;
